import { useEffect, useState } from "react";
import { Calendar, momentLocalizer } from "react-big-calendar";
import moment from "moment-timezone"; // Use moment-timezone for GMT+7
import "react-big-calendar/lib/css/react-big-calendar.css";
import { formatDateGMT7, generateOccurrences } from "../utils";
import { auth } from "../firebase";

moment.defineLocale("custom", {
  parentLocale: "en",
  longDateFormat: {
    LT: "HH:mm",
    LTS: "H:i:s",
    L: "YYYY-MM-DD",
    LL: "MMMM D, YYYY",
    LLL: "MMMM D, YYYY H:i",
    LLLL: "dddd, MMMM D, YYYY H:i",
  },
});
moment.locale("custom");

const localizer = momentLocalizer(moment);

const CalendarView = ({ room, bookings, onSelectEvent, onSelectSlot }) => {
  const [view, setView] = useState("week");
  const [date, setDate] = useState(new Date());
  const [roomName, setRoomName] = useState(room.name);
  const [error, setError] = useState(null);

  const generateAllEvents = () => {
    const allEvents = [];
    bookings.forEach((b) => {
      const eventBase = {
        id: b.id,
        title: `${b.name} - ${b.purpose || "Meeting"} (${room.name})`,
        allDay: false,
        email: b.email,
      };

      if (b.isRecurring) {
        const occurrences = generateOccurrences(
          new Date(b.date),
          new Date(b.recurrenceEndDate),
          b.recurrenceType,
          b.recurrenceDays
        );

        occurrences.forEach((date) => {
          allEvents.push({
            ...eventBase,
            start: new Date(`${formatDateGMT7(date)}T${b.startTime}`),
            end: new Date(`${formatDateGMT7(date)}T${b.endTime}`),
          });
        });
      } else {
        allEvents.push({
          ...eventBase,
          start: new Date(`${b.date}T${b.startTime}`),
          end: new Date(`${b.date}T${b.endTime}`),
        });
      }
    });
    return allEvents;
  };

  const events = generateAllEvents();

  const handleNavigate = (newDate) => {
    setDate(newDate);
  };

  const handleSelectEvent = (event) => {
    const currentUser = auth.currentUser;
    if (currentUser && currentUser.email === event.email) {
      const booking = bookings.find((b) => b.id === event.id);
      if (booking) {
        onSelectEvent(booking);
        setError(null);
        window.scrollTo(0, 0);
      }
    } else {
      setError("You are not the owner of this booking");
    }
  };

  const handleSelectSlot = (slotInfo) => {
    // Get current date and time in GMT+7
    const now = moment().tz("Asia/Ho_Chi_Minh").toDate();
    if (slotInfo.start < now) {
      setError("Cannot select a date or time in the past");
      return;
    }

    // Check if slot overlaps with any existing events
    const isSlotTaken = events.some((event) => {
      return slotInfo.start >= event.start && slotInfo.start < event.end;
    });

    if (!isSlotTaken) {
      // Format the start date in GMT+7 before passing to parent
      const formattedDate = formatDateGMT7(slotInfo.start);
      const startTime = slotInfo.start.toTimeString().slice(0, 5);
      onSelectSlot({
        start: new Date(`${formattedDate}T${startTime}`),
        email: auth.currentUser?.email || "",
      });
      setError(null); // Clear error on valid selection
    } else {
      setError("This time slot is already booked");
    }
  };

  useEffect(() => {
    setRoomName(room.name);
  }, [room]);

  const customFormats = {
    agendaDateFormat: "yyyy-MM-dd",
  };

  // Add slotPropGetter to style past time slots
  const slotPropGetter = (slotDate) => {
    const now = moment().tz("Asia/Ho_Chi_Minh").toDate();
    if (slotDate < now) {
      return {
        style: {
          backgroundColor: "#f0f0f0",
        },
      };
    }
    return {};
  };

  return (
    <div style={{ height: "550px", marginTop: "20px" }}>
      <h5>Calendar for {roomName}</h5>
      {error && (
        <div style={{ color: "red", marginBottom: "10px" }}>{error}</div>
      )}
      <Calendar
        localizer={localizer}
        events={events}
        startAccessor="start"
        endAccessor="end"
        view={view}
        onView={setView}
        views={["day", "week", "agenda"]}
        style={{ height: 500 }}
        date={date}
        onNavigate={handleNavigate}
        defaultView="week"
        formats={customFormats}
        onSelectEvent={handleSelectEvent}
        onSelectSlot={handleSelectSlot}
        selectable
        slotPropGetter={slotPropGetter}
      />
    </div>
  );
};

export default CalendarView;
